import { DateMarshaller } from 'app/constants/schemas/Date';
import { IImage, Image } from 'app/constants/schemas/Image';
import { IStaffProfile, StaffProfile } from 'app/constants/schemas/StaffProfile';
import { IText, Text } from 'app/constants/schemas/Text';
import vmClient from 'app/services/vmClient';
import { Entity, EntityStringId, IEntity, IEntityType } from 'app/types/entity';
import { Language, LanguageMarshaller } from 'app/constants/enums/Language';
import { IOrganizationReference, OrganizationReference } from './OrganizationReference';
import { ILessonBibliography, LessonBibliography } from './LessonBibliography';

export interface ILessonHeaderAttributes {
  lock_version: number;
  id: EntityStringId;
  lessonType: string;
  language: Language;
  version: number | null;
  updatedAt: Date | null;
  firstPublishedAt: Date | null;
  internalTags: string[];
  tags: string[];
  contentLevel: number | null;
  masterId: EntityStringId | null;
  hidden: boolean | null;
  tieUp: boolean | null;
  featured: boolean | null;
  publishedReleaseDate: Date | null;
  publishedLatest: boolean | null;
  exam: boolean | null;
}

export interface ILessonHeaderAssociations {
  image: IImage | null;
  titleText: IText;
  subtitleText: IText | null;
  introductionText: IText | null;
  publishedCreator: IStaffProfile | null;
  owner: IOrganizationReference;
  lessonBibliographies: ILessonBibliography[];
  authors: IStaffProfile[];
}

export interface ILessonHeader extends IEntity {
  attributes: ILessonHeaderAttributes;
  associations: ILessonHeaderAssociations;
}

export const lessonHeaderAttributes = {
  lessonType: {
    type: 'string',
    notNull: true,
  },
  language: {
    type: Language,
    marshaller: LanguageMarshaller,
    notNull: true,
  },
  version: 'number',
  updatedAt: {
    type: Date,
    marshaller: DateMarshaller,
  },
  firstPublishedAt: {
    type: Date,
    marshaller: DateMarshaller,
  },
  internalTags: {
    type: 'string',
    collection: true,
  },
  tags: {
    type: 'string',
    collection: true,
  },
  contentLevel: 'number',
  masterId: 'string',
  hidden: 'boolean',
  tieUp: 'boolean',
  featured: 'boolean',
  // Published lesson fields
  publishedReleaseDate: {
    type: Date,
    marshaller: DateMarshaller,
  },
  publishedLatest: 'boolean',
  exam: 'boolean',
};

export const lessonHeaderAssociations = {
  image: Image,
  titleText: {
    type: Text,
    notNull: true,
  },
  subtitleText: Text,
  introductionText: Text,

  // Published lesson associations
  publishedCreator: {
    type: StaffProfile,
    shared: true,
  },
  owner: {
    type: OrganizationReference,
    notNull: true,
    shared: true,
  },
  lessonBibliographies: {
    type: LessonBibliography,
    notNull: true,
    collection: true,
  },
  authors: {
    type: StaffProfile,
    notNull: true,
    collection: true,
    shared: true,
  },
};

export type LessonHeader = Entity<ILessonHeader>;
// defineClass is a class factory, normally classes create a runtime variable and identifier in the type namespace.
// eslint-disable-next-line @typescript-eslint/no-redeclare
export const LessonHeader: IEntityType<ILessonHeader> = vmClient.defineClass({
  typeName: 'LessonHeader',
  version: 4,
  lockable: true,
  persisted: true,
  attributes: lessonHeaderAttributes,
  associations: lessonHeaderAssociations,
});
